/**
 * Small conversions between admin form fields and API payloads: datetime-local
 * inputs ↔ ISO instants, comma strings ↔ tag arrays, and nullable coercions.
 */

import type { FieldValue } from "./AdminForm";

/** ISO instant → value for an <input type="datetime-local"> (local time). */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** datetime-local value → ISO instant (or null when empty/invalid). */
export function localInputToIso(local: string | null | undefined): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** Comma-separated string (or an array) → trimmed, non-empty tag array. */
export function parseTags(value: FieldValue | undefined): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value.map((t) => t.trim()).filter(Boolean);
  return String(value)
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Tag array → comma-separated string for an input. */
export function tagsToInput(tags: string[] | null | undefined): string {
  return (tags ?? []).join(", ");
}

/** Trim a form value to a non-empty string, or null. */
export function toNullableString(v: FieldValue | undefined): string | null {
  if (v == null || Array.isArray(v)) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
}

/** Coerce a form value to a finite number, or null. */
export function toNullableNumber(v: FieldValue | undefined): number | null {
  if (v == null || v === "" || Array.isArray(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** A checklist value → its string array (anything else → empty). */
export function toStringList(v: FieldValue | undefined): string[] {
  return Array.isArray(v) ? v : [];
}

/**
 * A readable title from an uploaded file's name: extension dropped, dashes and
 * underscores to spaces, runs of spaces collapsed. "reef_shark-02.JPG" →
 * "reef shark 02". Camera names like "IMG_2041" stay recognisable, which is
 * the point — they are placeholders to edit later, not guesses.
 */
export function titleFromFilename(name: string): string {
  const stem = name.replace(/\.[^.]+$/, "");
  const title = stem.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return title || "Untitled";
}
